/**
 * Mock logger for testing
 *
 * Provides a silent logger implementation that can be used in tests
 * to avoid console noise while maintaining the same interface as the real logger.
 *
 * Usage in vitest:
 * ```typescript
 * import { vi } from 'vitest';
 *
 * vi.mock('@platform/browser-logger', () => ({
 *   logger: createMockLogger(),
 * }));
 * ```
 *
 * Or for spying on calls:
 * ```typescript
 * import { vi } from 'vitest';
 * import { createMockLogger } from '@platform/browser-logger/mock';
 *
 * const mockLogger = createMockLogger();
 *
 * vi.mock('@platform/browser-logger', () => ({
 *   logger: mockLogger,
 * }));
 *
 * // Later in test
 * expect(mockLogger.info).toHaveBeenCalledWith('Expected message', { data: 'value' });
 * ```
 */

import type { Logger } from "./browser-logger";

type LogFn = (...args: unknown[]) => void;

/**
 * Creates a mock logger instance with all methods as no-ops
 *
 * @param spy - If true, uses vi.fn() for spying (requires vitest context). Default: false
 * @returns Mock logger instance
 */
export function createMockLogger(spy = false): Logger {
  const noop: LogFn = () => {
    /* intentionally empty */
  };

  // If spy is true and we're in a vitest context, try to use vi.fn()
  // Otherwise use no-op functions
  const mockFn = spy && typeof vi !== "undefined" ? vi.fn() : noop;

  return {
    debug: mockFn,
    info: mockFn,
    warn: mockFn,
    error: mockFn,
  };
}

/**
 * Pre-created silent mock logger (no-op, no spying)
 * Use this when you just want to silence logs without assertion needs
 */
export const mockLogger = createMockLogger(false);

// Allow vi to be undefined outside of test context
declare const vi: { fn(): LogFn } | undefined;
